/**
 * AuctionLobby.js
 *
 * The host's start screen for an Auction room, shown until the auction starts.
 * Built to be put up on a projector / TV, so everything is large:
 * - Where to join, and the room code
 * - The library being played (host picks it first, and can change it)
 * - Players, teams joined, budget per team, items up for auction
 * - Every team with its members, updating live as people join
 * - Lobby settings (host) and a summary of them
 * - Start Auction button
 *
 * Players don't see this; they see their own team screen (with the rules) until the host starts.
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Library, Play, Settings } from "lucide-react";
import toast from "react-hot-toast";
import { startAuction } from "../helpers/auctionLobby";
import { getAuctionSettings } from "../assets/Rules";
import LobbySettingsModal from "../Modals/LobbySettings";
import LibraryPickerModal from "../Modals/LibraryPicker";
import { getTeamColor, sortTeams } from "../assets/teamColors";

const AuctionLobby = ({ room, teams }) => {
    const [isStarting, setIsStarting] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    // The host picks a library before anything else
    const [showLibraryPicker, setShowLibraryPicker] = useState(!room.library);
    const library = room.library;
    const settings = getAuctionSettings(room);

    const roomCode = room.id.toUpperCase();
    const joinAddress = `${window.location.host}/auction`;

    const sortedTeams = sortTeams(teams);
    const teamsWithPlayers = teams.filter((team) => team.members?.length > 0);
    const totalPlayers = teams.reduce((sum, team) => sum + (team.members?.length || 0), 0);
    const budgetPerTeam = settings.budgetPerTeam;

    const stats = [
        { label: "Players",         value: totalPlayers },
        { label: "Teams joined",    value: `${teamsWithPlayers.length}/${teams.length}` },
        { label: "Budget per team", value: `₹${budgetPerTeam}` },
        { label: "Bids start at",   value: `₹${settings.startingBid}` },
        { label: "Bid step",        value: `₹${settings.bidStep}` },
        { label: "Items",           value: library?.itemCount ?? "—" },
    ];

    const settingsSummary = [
        `Buy ${settings.minItemsPerTeam}–${settings.maxItemsPerTeam} items, submit ${settings.itemsToSubmit}`,
        `Bids from ₹${settings.startingBid}, in steps of ₹${settings.bidStep}`,
        `At least ${settings.minTeams} teams`,
        ...Object.entries(settings.attributeRules).flatMap(([attribute, rule]) => [
            rule.minDifferent > 0 && `At least ${rule.minDifferent} different ${attribute}`,
            rule.maxPerValue !== null && rule.maxPerValue !== undefined && `Max ${rule.maxPerValue} per ${attribute}`,
        ]).filter(Boolean),
        settings.breakAfter > 0 ? `Break every ${settings.breakAfter} sales` : "No breaks",
        settings.bringBackUnsold ? "Unsold items come back" : "Unsold items stay unsold",
        settings.showBudgets ? "Budgets public" : "Budgets hidden",
        settings.showSales ? "Sales public" : "Sales hidden",
        settings.showInventories ? "Inventories public" : "Inventories hidden",
    ];

    const handleStart = async () => {
        setIsStarting(true);
        const started = await startAuction(room.id);
        if (!started) {
            toast.error("Couldn't start the auction");
            setIsStarting(false);
        }
        // On success, the room listener moves everyone out of the lobby
    };

    return (
        <div className="w-full max-w-[110rem] mx-auto px-6 md:px-12 pt-24 pb-12 text-white">

            {/* --- Join instructions + room code --- */}
            <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-10">
                <div>
                    <p className="text-2xl md:text-3xl text-zinc-400">
                        Go to <span className="text-white font-semibold">{joinAddress}</span>
                    </p>
                    <p className="text-2xl md:text-3xl text-zinc-400 mt-1">
                        tap <span className="text-white font-semibold">Join a Game</span> and enter
                    </p>
                    <div className="mt-4 font-mono font-extrabold tracking-[0.15em] text-yellow leading-none text-[clamp(4rem,11vw,11rem)]">
                        {roomCode}
                    </div>
                </div>

                {/* --- Start --- */}
                <div className="flex flex-col items-stretch xl:items-end gap-4">
                    <button
                        onClick={handleStart}
                        disabled={isStarting || !library || teamsWithPlayers.length < settings.minTeams}
                        className="inline-flex items-center justify-center gap-4 rounded-3xl bg-gradient-to-br from-yellow via-peach to-coral px-14 py-8 text-4xl md:text-5xl font-extrabold text-black shadow-2xl transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:hover:scale-100"
                    >
                        <Play className="w-10 h-10 md:w-12 md:h-12" fill="currentColor" />
                        {isStarting ? "Starting..." : "Start Auction"}
                    </button>
                    <span className="text-xl text-zinc-400 text-center xl:text-right">
                        {!library
                            ? "Choose a library first"
                            : teamsWithPlayers.length < settings.minTeams
                                ? `Waiting for ${settings.minTeams - teamsWithPlayers.length} more team${settings.minTeams - teamsWithPlayers.length === 1 ? "" : "s"} to join`
                                : "Start when everyone has settled in"}
                    </span>
                    <button
                        onClick={() => setShowSettings(true)}
                        className="inline-flex items-center justify-center gap-3 rounded-2xl border border-zinc-700 px-6 py-3 text-xl text-zinc-200 hover:bg-zinc-900 transition-colors"
                    >
                        <Settings size={22} /> Lobby settings
                    </button>
                </div>
            </div>

            {/* --- Library --- */}
            <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-3xl border border-zinc-800 bg-zinc-950 px-8 py-6">
                <div>
                    <div className="text-xl md:text-2xl text-zinc-400">Library</div>
                    {library ? (
                        <>
                            <div className="mt-1 text-4xl md:text-5xl font-extrabold">{library.name}</div>
                            <div className="mt-2 text-xl text-zinc-400">
                                ID <span className="font-mono text-white tracking-wider">{library.code.toUpperCase()}</span>
                                {" · "}judged on <span className="text-white">{library.scoreAttribute}</span>
                            </div>
                        </>
                    ) : (
                        <div className="mt-1 text-4xl md:text-5xl font-extrabold text-zinc-600">Not chosen yet</div>
                    )}
                </div>
                <button
                    onClick={() => setShowLibraryPicker(true)}
                    className="inline-flex items-center justify-center gap-3 rounded-2xl border border-zinc-700 px-6 py-3 text-xl text-zinc-200 hover:bg-zinc-900 transition-colors"
                >
                    <Library size={22} /> {library ? "Change library" : "Choose library"}
                </button>
            </div>

            {/* --- Settings summary --- */}
            <div className="mt-8 flex flex-wrap gap-3">
                {settingsSummary.map((item) => (
                    <span key={item} className="rounded-full border border-zinc-800 px-5 py-2 text-xl text-zinc-300">
                        {item}
                    </span>
                ))}
            </div>

            {/* --- Stats --- */}
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-6">
                {stats.map(({ label, value }) => (
                    <div key={label} className="rounded-3xl border border-zinc-800 bg-zinc-950 px-8 py-6">
                        <div className="text-xl md:text-2xl text-zinc-400">{label}</div>
                        <div className="mt-2 text-5xl md:text-6xl font-extrabold">{value}</div>
                    </div>
                ))}
            </div>

            {/* --- Teams --- */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-6">
                {sortedTeams.map((team) => (
                    <motion.div
                        key={team.id}
                        layout
                        className="rounded-3xl border border-zinc-800 bg-zinc-950 overflow-hidden"
                    >
                        <div className={`h-3 ${getTeamColor(team).bg}`} />
                        <div className="p-6">
                            <div className="flex items-baseline justify-between gap-4">
                                <span className="text-3xl font-bold">{team.name}</span>
                                <span className="text-2xl font-semibold text-zinc-500">{team.members?.length || 0}</span>
                            </div>

                            {team.members?.length > 0 ? (
                                <ul className="mt-5 space-y-2 text-2xl text-zinc-200">
                                    {team.members.map((member) => (
                                        <motion.li
                                            key={member.userID || member.uid}
                                            initial={{ opacity: 0, x: -12 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="truncate"
                                        >
                                            {member.displayName || "Anonymous"}
                                        </motion.li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="mt-5 text-2xl text-zinc-600">Waiting…</p>
                            )}
                        </div>
                    </motion.div>
                ))}
            </div>

            {showSettings && (
                <LobbySettingsModal
                    room={room}
                    teams={teams}
                    settings={settings}
                    onClose={() => setShowSettings(false)}
                />
            )}

            {showLibraryPicker && (
                <LibraryPickerModal
                    room={room}
                    onClose={() => setShowLibraryPicker(false)}
                />
            )}
        </div>
    );
};

export default AuctionLobby;
